import { useCallback, useState } from 'react';

import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

const paginationItemBaseSx = {
    border: '1px solid #D4D4D4',
    borderRadius: '999px',
    minWidth: 34,
    height: 34,
    color: '#535353',
    '&.Mui-selected': {
        borderColor: '#3F8EE8',
        color: '#3F8EE8',
        backgroundColor: '#FFFFFF',
    },
    '&.Mui-selected:hover': {
        backgroundColor: '#F4F9FF',
    },
};

const renderPaginationItem = (item: any) => {
    const isDirectionButton = ['first', 'previous', 'next', 'last'].includes(item.type);

    return (
        <PaginationItem
            {...item}
            sx={{
                ...paginationItemBaseSx,
                ...(isDirectionButton && {
                    backgroundColor: item.disabled ? '#A3A3A3' : '#3F8EE8',
                    color: '#FFFFFF',
                    opacity: 1,
                    '&:hover': {
                        backgroundColor: item.disabled ? '#A3A3A3' : '#327ED5',
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#A3A3A3',
                        color: '#FFFFFF',
                        opacity: 1,
                    },
                }),
            }}
        />
    );
};

const pageInputSx = (width: string) => ({
    width,
    minWidth: '66px',
    '& .MuiOutlinedInput-root': {
        width,
        minWidth: '66px',
        height: '34px',
        borderRadius: '999px',
        color: '#D4D4D4',
        px: '8px',
        py: '16px',
        boxSizing: 'border-box',
        '& fieldset': {
            borderColor: '#D4D4D4',
        },
        '&:hover fieldset': {
            borderColor: '#D4D4D4',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#D4D4D4',
        },
    },
    '& .MuiInputBase-input': {
        p: 0,
        fontSize: '12px',
        color: '#535353',
        textAlign: 'left',
        '&::placeholder': {
            color: '#D4D4D4',
            opacity: 1,
        },
    },
});

interface TablePaginationProps {
    page: number;
    pageCount: number;
    onChange: (event: unknown, page: number) => void;
}

/** Desktop pagination bar for record tables: page buttons plus a "Go to page" box. */
function TablePagination({ page, pageCount, onChange }: TablePaginationProps) {
    const [pageInputValue, setPageInputValue] = useState('');

    const handleGoToPage = useCallback(() => {
        const trimmedValue = pageInputValue.trim();
        if (!trimmedValue) {
            return;
        }

        const requestedPage = Number.parseInt(trimmedValue, 10);
        if (Number.isNaN(requestedPage)) {
            return;
        }

        const maxPage = pageCount > 0 ? pageCount : 1;
        onChange(null, Math.min(Math.max(requestedPage, 1), maxPage));
    }, [onChange, pageCount, pageInputValue]);

    const handlePageInputKeyDown = (event: any) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleGoToPage();
        }
    };

    return (
        <Stack direction='row' spacing={2} alignItems='center' justifyContent='center' marginTop={2}>
            <Pagination
                count={pageCount}
                showFirstButton
                showLastButton
                variant='outlined'
                shape='rounded'
                page={page}
                onChange={onChange}
                renderItem={renderPaginationItem}
            />
            <Stack direction='row' spacing={1} alignItems='center'>
                <span style={{ fontSize: '12px', color: '#535353' }}>Go to page: </span>
                <TextField
                    variant='outlined'
                    size='small'
                    placeholder='e.g. 200'
                    value={pageInputValue}
                    onChange={(event: any) => setPageInputValue(event.target.value)}
                    onKeyDown={handlePageInputKeyDown}
                    inputProps={{ 'aria-label': 'Pagination input field' }}
                    sx={pageInputSx(`${Math.max(pageInputValue.length, 8)}ch`)}
                />
                <button
                    type='button'
                    onClick={handleGoToPage}
                    className='flex h-[34px] w-[34px] items-center justify-center rounded-full bg-[#3F8EE8] text-xs font-bold text-white'
                    aria-label='Go to page'
                >
                    Go
                </button>
            </Stack>
        </Stack>
    );
}

export default TablePagination;
